import { Feather, FontAwesome } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import React, { useState, useEffect } from "react";
import { Dimensions, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Centered,
  ErrorMessage,
  Input,
  StepHeader,
} from "../../../components/all";
import { resetEmptyFields } from "../../../reducers/auth/register";
import { resetEmptyFields as resetProfileEmptyFields } from "../../../reducers/profile/profile";
import Styles from "../../../constants/styles";
import { useActionSheet } from "@expo/react-native-action-sheet";

export default function Step3({
  setFocusedInput,
  focusedInput,
  setField,
  allFields,
  editProfile,
  setEditProfileImage,
  isWeb,
  currentStep,
}) {
  const { showActionSheetWithOptions } = useActionSheet();

  const dispatch = useDispatch();
  const { isDoctor, country } = useSelector((state) => state.user);
  const { errorFields } = useSelector(
    (state) => state[editProfile ? "profile" : "register"]
  );
  const currentCountry = allFields.country || country || 'MY';
  
  // Helper function for country-specific ID document placeholder
  const getIdDocumentPlaceholder = (countryCode) => {
    const placeholders = {
      'MY': 'IC/Passport Number',
      'SG': 'NRIC/Passport Number',
      'PH': 'Valid ID',
      'ID': 'KTP/Passport Number'
    };
    return placeholders[countryCode] || 'National ID/Passport Number';
  };

  const getIdDocumentErrorMessage = (countryCode) => {
    const messages = {
      'MY': 'Please enter a valid IC/Passport number.',
      'SG': 'Please enter a valid NRIC/Passport number.',
      'PH': 'Please enter a valid ID.',
      'ID': 'Please enter a valid KTP/Passport number.'
    };
    return messages[countryCode] || 'Please enter a valid national ID/passport number.';
  };
  
  const setErrorFields = () => {
    dispatch(editProfile ? resetProfileEmptyFields() : resetEmptyFields());
  };

  const input = {
    first_name: "first_name",
    nric_passport_number: "nric_passport_number",
    about: "about",
    phone_number: "phone_number",
    profile_image: "profile_image",
    emergency_number: "emergency_number",
    emergency_name: "emergency_name",
  };

  const [imageUrl, setImageUrl] = useState(allFields?.profile_image || null);
  const onPressImage = async () => {
    const options = ["Take Photo", "Photo Library", "Cancel"];
    const cancelButtonIndex = 2;
    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
      },
      (selectedIndex) => {
        switch (selectedIndex) {
          case 0:
            console.log("select take photo");
            onOpenCamera();
            break;
          case 1:
            onPickImage();
            break;
          case cancelButtonIndex:
        }
      }
    );
  };
  const onOpenCamera = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      alert(
        "Sorry, we need camera permissions to make this work. Please go to Settings > Permissions and allow it."
      );
    } else {
      let result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.5,
        base64: true,
        height: 100,
        width: 100,
      });
      if (result) {
        setImageUrl(result?.assets[0].base64);
        setField(input.profile_image, result?.assets[0].base64);
      }
    }
  };

  const onPickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      alert(
        "Sorry, we need camera roll permissions to make this work. Please go to Settings > Permissions and allow it."
      );
    } else {
      let result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.5,
        base64: true,
        height: 100,
        width: 100,
      });
      if (result) {
        setImageUrl(result?.assets[0].base64);
        setField(input.profile_image, result?.assets[0].base64);
      }
    }
  };

  useEffect(() => {
    if (allFields?.profile_image) {
      setImageUrl(allFields?.profile_image);
    }
  }, [allFields?.profile_image]);

  const overrideError = (inputType) => {
    if (editProfile) {
      if (allFields[inputType] === null || allFields[inputType] === "") {
        return true;
      }
    } else {
      return errorFields.includes(inputType);
    }
  };
  if (isWeb && currentStep !== 2) return <></>;

  return (
    <View
      style={{
        ...Styles.webSmallContainer,
      }}
    >
      <StepHeader
        label={
          editProfile
            ? "Update Profile"
            : isDoctor
            ? `Personal \nDetails`
            : `Facility Owner's \nInformation`
        }
        icon={<Feather name="user" size={24} color="black" />}
        subtitle={
          editProfile
            ? isDoctor
              ? "Tell us more about yourself"
              : "How can we reach you?"
            : `How can we address you?`
        }
        showImage
        imageUrl={imageUrl}
        imageError={errorFields.includes(input.profile_image)}
        onPressImage={() => {
          setEditProfileImage && setEditProfileImage(true);
          onPressImage();
        }}
        isProfile
      />
      <Centered
        style={{ width: Dimensions.get("window").width > 480 ? 480 : "100%" }}
      >
        <Input
          onChangeText={(val) => {
            setField(input.first_name, val);
          }}
          value={allFields[input.first_name]}
          icon={<FontAwesome name="user" size={14} color="grey" />}
          placeholder={isDoctor ? "Full Name" : "App Manager Name"}
          containerStyle={{
            width: "90%",
            marginTop: 10,
          }}
          onBlur={() => setFocusedInput(null)}
          onFocus={() => {
            setErrorFields();
            setFocusedInput(input.first_name);
          }}
          error={overrideError(input.first_name)}
          focused={focusedInput === input.first_name}
          inputStyle={{ width: "50%" }}
          autoCapitalize="words"
        />
        {isDoctor ? (
          <Input
            onChangeText={(val) => {
              setField(input.nric_passport_number, val);
            }}
            value={allFields[input.nric_passport_number]}
            placeholder={getIdDocumentPlaceholder(currentCountry)}
            icon={<FontAwesome name="id-card-o" size={14} color="grey" />}
            containerStyle={{ width: "90%", marginTop: 10 }}
            onBlur={() => setFocusedInput(null)}
            onFocus={() => {
              setErrorFields();
              setFocusedInput(input.nric_passport_number);
            }}
            error={
              overrideError(input.nric_passport_number) ||
              errorFields.includes("nric_passport_numberError")
            }
            focused={focusedInput === input.nric_passport_number}
          />
        ) : (
          <>
            <Input
              onChangeText={(val) => {
                setField(input.phone_number, val);
              }}
              value={allFields[input.phone_number]}
              placeholder="App Manager Mobile No."
              keyboardType="numeric"
              icon={<FontAwesome name="phone" size={14} color="grey" />}
              containerStyle={{ width: "90%", marginTop: 10 }}
              onBlur={() => setFocusedInput(null)}
              maxLength={15}
              onFocus={() => {
                setErrorFields();
                setFocusedInput(input.phone_number);
              }}
              error={overrideError(input.phone_number)}
              focused={focusedInput === input.phone_number}
            />
            <Input
              onChangeText={(val) => {
                setField(input.emergency_name, val);
              }}
              value={allFields[input.emergency_name]}
              placeholder="Assistant App Manager Name"
              icon={<FontAwesome name="user" size={14} color="grey" />}
              containerStyle={{ width: "90%", marginTop: 10 }}
              onBlur={() => setFocusedInput(null)}
              onFocus={() => {
                setErrorFields();
                setFocusedInput(input.emergency_name);
              }}
              error={errorFields.includes(input.emergency_name)}
              focused={focusedInput === input.emergency_name}
              autoCapitalize="words"
            />
            <Input
              onChangeText={(val) => {
                setField(input.emergency_number, val);
              }}
              value={allFields[input.emergency_number]}
              placeholder="Assistant App Manager Mobile No."
              keyboardType="numeric"
              icon={<FontAwesome name="mobile-phone" size={14} color="grey" />}
              containerStyle={{ width: "90%", marginTop: 10 }}
              onBlur={() => setFocusedInput(null)}
              maxLength={15}
              onFocus={() => {
                setErrorFields();
                setFocusedInput(input.emergency_number);
              }}
              error={errorFields.includes(input.emergency_number)}
              focused={focusedInput === input.emergency_number}
            />
          </>
        )}
        {!isDoctor ? null : (
          <Input
            onChangeText={(val) => {
              setField(input.about, val);
            }}
            value={allFields[input.about]}
            icon={<Feather name="info" size={14} color="grey" />}
            placeholder="Tell us about yourself"
            containerStyle={{
              width: "90%",
              marginTop: 10,
              marginBottom: editProfile ? 4 : 0,
            }}
            onBlur={() => setFocusedInput(null)}
            onFocus={() => {
              setErrorFields([]);
              setFocusedInput(input.about);
            }}
            error={overrideError(input.about)}
            focused={focusedInput === input.about}
            maxLength={400}
            multiline={true}
            isAbout
          />
        )}
      </Centered>
      <View style={{ margin: 10, marginLeft: 24, marginBottom: 20 }}>
        <ErrorMessage
          visible={errorFields.includes(input.first_name)}
          label={"Please fill in the required fields."}
        />
        <ErrorMessage
          visible={errorFields.includes("nric_passport_numberError")}
          label={getIdDocumentErrorMessage(currentCountry)}
        />
        <ErrorMessage
          visible={errorFields.includes("phoneError")}
          label={"Please enter a valid Mobile Number."}
        />
      </View>
    </View>
  );
}
